import React, {useEffect, useMemo} from 'react';
import {Text, Icon, Button, TextInput} from '@/ui-kits';
import {useFormik} from 'formik';
import {useDispatch, useSelector} from 'react-redux';
import {useToast, useRole} from '@/hooks';
import {ErrorCodes, getSuccessMessage, SuccessCodes, getErrorMessage} from '@/utils/getMessages';
import {palette} from '@/theme/Palette';
import {ContactFormValidation} from '@/utils/schema';
import {SubmitHelpRequestRequestPayload} from '@/interface';
import {
  submitHelpRequestRequest,
  resetSupportAdminMessage,
} from '@/services/redux/slices/supportAdminSlice';
import {
  submitHospitalHelpRequestRequest,
  resetHospitalAdminMessage,
} from '@/services/redux/slices/hospitalAdminSlice';
import {
  supportAdminStatus,
  supportAdminSuccessSelector,
  supportAdminFailureSelector,
  hospitalAdminLoadingSelector,
  hospitalAdminSuccessSelector,
  hospitalAdminFailureSelector,
  authDataSelector,
} from '@/services/redux/selectors';
import {Images, Icons} from '@/constants/defaults';


export const HelpScreen: React.FC = () => {
  // ==============================
  // hooks
  // ==============================
  const dispatch = useDispatch();
  const {showToast} = useToast();
  const {isSpecialist, isPatient, isHospitalPoc} = useRole();
  const authData = useSelector(authDataSelector);

  // ==============================
  // Initial values
  // ==============================
  const initialContactValues = useMemo<SubmitHelpRequestRequestPayload>(() => ({
    contact_email: authData?.email_id || '',
    question: '',
  }), [authData]);

  // ==============================
  // Redux selectors
  // ==============================
  const supportAdminLoading = useSelector(supportAdminStatus);
  const supportAdminSuccess = useSelector(supportAdminSuccessSelector) as SuccessCodes | false;
  const supportAdminFailure = useSelector(supportAdminFailureSelector) as ErrorCodes | false;
  
  const hospitalAdminLoading = useSelector(hospitalAdminLoadingSelector);
  const hospitalAdminSuccess = useSelector(hospitalAdminSuccessSelector) as SuccessCodes | false;
  const hospitalAdminFailure = useSelector(hospitalAdminFailureSelector) as ErrorCodes | false;

  const isLoading = isHospitalPoc ? hospitalAdminLoading : supportAdminLoading;

  // ==============================
  // state
  // ==============================

  // ==============================
  // functions
  // ==============================

  const {values, errors, touched, handleChange, handleBlur, handleSubmit, resetForm} = useFormik({
    initialValues: initialContactValues,
    enableReinitialize: true,
    validationSchema: ContactFormValidation,
    onSubmit: (values: SubmitHelpRequestRequestPayload) => {
      if (isHospitalPoc) {
        dispatch(submitHospitalHelpRequestRequest(values));
      } else {
        dispatch(submitHelpRequestRequest(values));
      }
    },
  });

  // ==============================
  // effects
  // ==============================
  useEffect(() => {
    const success = isHospitalPoc ? hospitalAdminSuccess : supportAdminSuccess;
    const failure = isHospitalPoc ? hospitalAdminFailure : supportAdminFailure;
    const resetMessage = isHospitalPoc ? resetHospitalAdminMessage : resetSupportAdminMessage;

    if (success) {
      const successCode = String(success);
      showToast(getSuccessMessage(success as SuccessCodes), 'success');
      if (successCode === 'S-10090') {
        resetForm();
      }
    }

    if (failure) {
      showToast(getErrorMessage(failure), 'failure');
    }

    return () => {
      dispatch(resetMessage());
    };
  }, [isHospitalPoc, hospitalAdminSuccess, hospitalAdminFailure, supportAdminSuccess, supportAdminFailure, showToast, resetForm, dispatch]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Help Image - Show for patients, specialists, and Hospital POC */}
      {(isPatient || isSpecialist || isHospitalPoc) && (
        <div className="w-full mb-8 sm:mb-12">
          <img 
            src={Images.help_mobile} 
            alt="Help" 
            className="w-full h-auto sm:hidden"
          />
          <img 
            src={Images.help} 
            alt="Help" 
            className="w-full h-auto hidden sm:block"
          />
        </div>
      )}
      
      {/* Legal & Medical Disclaimer - Show for both patients and specialists */}
      {(isPatient || isSpecialist) && (
        <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl border-l-4 border border-ui_input_border border-l-ui_blue p-6 sm:p-8 shadow-sm">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="size-10 sm:size-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <img src={Icons.exclametory_mark} alt="Exclamation" className="size-7 sm:size-8" />
                </div>
                <Text variant="heading" className="!text-xl sm:!text-2xl md:!text-3xl bg-gradient-to-r from-ui_blue to-ui_blue_light bg-clip-text text-transparent !font-FigtreeBold">
                  Important Legal & Medical Disclaimer
                </Text>
              </div>

              {/* First Paragraph */}
              <Text variant="body" className="!text-deep_blue !text-sm sm:!text-base md:!text-lg mb-4 leading-relaxed !font-FigtreeRegular">
                The Second Opinion services provided through this platform are intended for informational and advisory purposes only. They do not constitute a medical diagnosis, treatment plan, or replacement for in-person medical consultation.
              </Text>

              {/* Second Paragraph */}
              <Text variant="body" className="!text-deep_blue !text-sm sm:!text-base md:!text-lg mb-4 leading-relaxed !font-FigtreeRegular">
                By using this platform, users acknowledge and agree that all medical decisions remain their sole responsibility and should be made in consultation with their primary treating physician.
              </Text>

              {/* Emergency Notice */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-5 mt-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <img src={Icons.emergency_contact} alt="Emergency" className="size-5 sm:size-6" />
                  </div>
                  <Text variant="body" className="text-ui_input_border_dark !text-sm sm:!text-base md:!text-lg leading-relaxed font-FigtreeMeduim">
                    In case of a medical emergency, users must seek immediate care from qualified healthcare professionals or emergency services.
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help & Usage Guidelines - Patients: 3 cards, Specialists: 2 cards */}
      {(isPatient || isSpecialist) && (
        <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8">
          <div className="max-w-6xl mx-auto">
            {/* Title */}
            <Text variant="heading" className="!text-2xl sm:!text-2xl md:!text-3xl text-center mb-6 sm:mb-8 md:mb-12 bg-gradient-to-r from-ui_blue to-ui_blue_light bg-clip-text text-transparent !font-FigtreeBold">
              Help & Usage Guidelines
            </Text>

            {/* Patient Cards - 3 cards */}
            {isPatient && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-2 3xl:grid-cols-3 gap-4 sm:gap-6">
                {/* Account Access Card */}
                <div className="bg-ui_sky_blue rounded-3xl border border-profile_border_light_blue p-4 sm:p-5 md:p-6 shadow-sm">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="size-10 sm:size-12 md:size-16 rounded-full flex items-center justify-center flex-shrink-0">
                      <img src={Icons.help_p_1} alt="Account Access" className="size-10 sm:size-12 md:size-14" />
                    </div>
                    <Text variant="heading" className="!text-xl sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue !font-FigtreeBold break-words flex-1 min-w-0">
                      Account Access
                    </Text>
                  </div>
                  <ul className="space-y-4 sm:space-y-5 md:space-y-6 text-deep_blue leading-relaxed">
                    <li className="flex items-start mt-4 sm:mt-6 md:mt-8">
                      <span className="mr-2">•</span>
                      <span className='!text-deep_blue !text-sm sm:!text-sm md:!text-base !font-FigtreeRegular'>Patients may create an account using a valid email address. Account access is protected through secure authentication mechanisms.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span className='!text-deep_blue !text-sm sm:!text-sm md:!text-base !font-FigtreeRegular'>Personal information may be updated at any time through the profile section.</span>
                    </li>
                  </ul>
                </div>

                {/* Medical Document Submission Card */}
                <div className="bg-ui_sky_blue rounded-3xl border border-profile_border_light_blue p-4 sm:p-5 md:p-6 shadow-sm">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="size-10 sm:size-12 md:size-16 rounded-full flex items-center justify-center flex-shrink-0">
                      <img src={Icons.help_p_2} alt="Medical Document Submission" className="size-10 sm:size-12 md:size-14" />
                    </div>
                    <Text variant="heading" className="!text-xl sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue !font-FigtreeBold break-words flex-1 min-w-0">
                      Medical Document Submission
                    </Text>
                  </div>
                  <ul className="space-y-4 sm:space-y-5 md:space-y-6 text-deep_blue leading-relaxed">
                    <li className="flex items-start mt-4 sm:mt-5 md:mt-6">
                      <span className="mr-2">•</span>
                      <span className='!text-deep_blue !text-sm sm:!text-sm md:!text-base !font-FigtreeRegular'>Patients may submit medical documents solely for the purpose of obtaining an independent second opinion.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span className='!text-deep_blue !text-sm sm:!text-sm md:!text-base !font-FigtreeRegular'>Uploaded documents are reviewed only by assigned specialists and authorized personnel.</span>
                    </li>
                  </ul>
                </div>

                {/* Data Privacy Card */}
                <div className="bg-ui_sky_blue rounded-3xl border border-profile_border_light_blue p-4 sm:p-5 md:p-6 shadow-sm">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="size-10 sm:size-12 md:size-16 rounded-full flex items-center justify-center flex-shrink-0">
                      <img src={Icons.help_p_3} alt="Data Privacy" className="size-10 sm:size-12 md:size-14" />
                    </div>
                    <Text variant="heading" className="!text-xl sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue !font-FigtreeBold break-words flex-1 min-w-0">
                      Data Privacy
                    </Text>
                  </div>
                  <ul className="space-y-4 sm:space-y-5 md:space-y-6 text-deep_blue leading-relaxed">
                    <li className="flex items-start mt-4 sm:mt-5 md:mt-6">
                      <span className="mr-2">•</span>
                      <span className='!text-deep_blue !text-sm sm:!text-sm md:!text-base !font-FigtreeRegular'>All patient data is handled in accordance with HIPAA and applicable data protection regulations.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span className='!text-deep_blue !text-sm sm:!text-sm md:!text-base !font-FigtreeRegular'>Access is restricted, logged, and monitored.</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Specialist Cards - 2 cards */}
            {isSpecialist && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 sm:items-stretch">
                {/* Profile Verification Card */}
                <div className="bg-ui_sky_blue rounded-3xl border border-profile_border_light_blue p-4 sm:p-5 md:p-6 shadow-sm w-full sm:w-auto sm:max-w-md flex flex-col">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="size-10 sm:size-12 md:size-16 rounded-full flex items-center justify-center flex-shrink-0">
                      <img src={Icons.help_p_1} alt="Profile Verification" className="size-10 sm:size-12 md:size-14" />
                    </div>
                    <Text variant="heading" className="!text-xl sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue !font-FigtreeBold break-words flex-1 min-w-0">
                      Profile Verification
                    </Text>
                  </div>
                  <ul className="space-y-4 sm:space-y-5 md:space-y-6 text-deep_blue leading-relaxed flex-1">
                    <li className="flex items-start mt-2 sm:mt-3">
                      <span className="mr-2">•</span>
                      <span className='!text-deep_blue !text-sm sm:!text-sm md:!text-base !font-FigtreeRegular'>Specialists are required to submit valid credentials and professional details.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span className='!text-deep_blue !text-sm sm:!text-sm md:!text-base !font-FigtreeRegular'>Profiles are subject to verification and approval prior to activation.</span>
                    </li>
                  </ul>
                </div>

                {/* Confidentiality Card */}
                <div className="bg-ui_sky_blue rounded-3xl border border-profile_border_light_blue p-4 sm:p-5 md:p-6 shadow-sm w-full sm:w-auto sm:max-w-md flex flex-col">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="size-10 sm:size-12 md:size-16 rounded-full flex items-center justify-center flex-shrink-0">
                      <img src={Icons.help_p_3} alt="Confidentiality" className="size-10 sm:size-12 md:size-14" />
                    </div>
                    <Text variant="heading" className="!text-xl sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue !font-FigtreeBold break-words flex-1 min-w-0">
                      Confidentiality
                    </Text>
                  </div>
                  <ul className="space-y-4 sm:space-y-5 md:space-y-6 text-deep_blue leading-relaxed flex-1">
                    <li className="flex items-start mt-2 sm:mt-3">
                      <span className="mr-2">•</span>
                      <span className='!text-deep_blue !text-sm sm:!text-sm md:!text-base !font-FigtreeRegular'>Specialists are bound by confidentiality obligations.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span className='!text-deep_blue !text-sm sm:!text-sm md:!text-base !font-FigtreeRegular'>Specialists must not store, share, or disclose patient information outside the platform.</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Help & Usage Guidelines - Hospital POC */}
      {isHospitalPoc && (
        <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8 bg-[#F9FAFB]">
          <div className="max-w-6xl mx-auto">
            {/* Title */}
            <Text variant="heading" className="!text-2xl sm:!text-3xl md:!text-4xl text-center mb-8 sm:mb-12 text-deep_blue !font-FigtreeBold">
              Help & Usage Guidelines
            </Text>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {/* Admin Oversight Card */}
              <div className="bg-ui_sky_blue rounded-3xl border border-profile_border_light_blue p-4 sm:p-5 md:p-6 shadow-sm">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div className="size-10 sm:size-12 md:size-16 rounded-full flex items-center justify-center flex-shrink-0">
                    <img src={Icons.admin_oversight} alt="Admin Oversight" className="size-10 sm:size-12 md:size-14" />
                  </div>
                  <Text variant="heading" className="!text-xl sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue !font-FigtreeBold break-words flex-1 min-w-0">
                    Admin Oversight
                  </Text>
                </div>
                <ul className="space-y-4 sm:space-y-5 md:space-y-6 text-deep_blue leading-relaxed">
                  <li className="flex items-start mt-2 sm:mt-3">
                    <span className="mr-2">•</span>
                    <span className='!text-deep_blue !text-sm sm:!text-sm md:!text-base !font-FigtreeRegular'>Administrators are responsible for specialist verification, consultation oversight, and compliance enforcement.</span>
                  </li>
                </ul>
              </div>

              {/* Audit Logging Card */}
              <div className="bg-ui_sky_blue rounded-3xl border border-profile_border_light_blue p-4 sm:p-5 md:p-6 shadow-sm">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div className="size-10 sm:size-12 md:size-16 rounded-full flex items-center justify-center flex-shrink-0">
                    <img src={Icons.audit_logging} alt="Audit Logging" className="size-10 sm:size-12 md:size-14" />
                  </div>
                  <Text variant="heading" className="!text-xl sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue !font-FigtreeBold break-words flex-1 min-w-0">
                    Audit Logging
                  </Text>
                </div>
                <ul className="space-y-4 sm:space-y-5 md:space-y-6 text-deep_blue leading-relaxed">
                  <li className="flex items-start mt-2 sm:mt-3">
                    <span className="mr-2">•</span>
                    <span className='!text-deep_blue !text-sm sm:!text-sm md:!text-base !font-FigtreeRegular'>All platform activities are logged for audit purposes. Administrators may review logs to ensure regulatory compliance.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8">
        <div className="max-w-6xl mx-auto">
        {/* Still need help section */}
        <div
          className="rounded-lg border border-ui_input_border p-6 sm:p-8"
          style={{background: `linear-gradient(to bottom, #FFFFFF, ${palette.ui_sky_blue})`}}>
          <div className="flex flex-col items-center mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="size-12 sm:size-14 rounded-full bg-ui_sky_blue flex items-center justify-center flex-shrink-0">
                <Icon type="email_clear" className="size-6 sm:size-8" />
              </div>
              <Text variant="heading" className="!text-2xl sm:!text-3xl text-deep_blue">
                Still need help?
              </Text>
            </div>
            <Text variant="body" className="!text-md sm:!text-xl !font-FigtreeMedium !text-deep_blue text-center max-w-2xl">
              Can't find what you're looking for?
            </Text>
            <Text variant="body" className="!text-md sm:!text-xl !font-FigtreeMedium !text-deep_blue text-center max-w-2xl">
              Send us a message and we'll get back to you soon.
            </Text>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="flex flex-col items-center">
            <div className="flex flex-col gap-4 sm:gap-6 w-full max-w-md mt-4 sm:mt-4">
              <TextInput
                name="contact_email"
                label="Email Address"
                required
                labelClassName="!font-FigtreeSemiBold !text-deep_blue"
                placeholder="your.email@example.com"
                value={values.contact_email}
                error={errors.contact_email}
                touched={touched.contact_email}
                onChangeText={handleChange('contact_email')}
                onBlur={handleBlur('contact_email')}
                wrapperClassName="bg-white"
              />
              <TextInput
                name="question"
                label="Your Question"
                required
                labelClassName="!font-FigtreeSemiBold !text-deep_blue"
                multiline
                placeholder="Please describe your question or issue in detail..."
                value={values.question}
                error={errors.question}
                touched={touched.question}
                onChangeText={handleChange('question')}
                onBlur={handleBlur('question')}
                wrapperClassName="bg-white"
              />
              <Button
                type="submit"
                title="Submit Question"
                variant="primary"
                disabled={!values.contact_email || !values.question || isLoading}
                isLoading={isLoading}
                className="w-full sm:w-auto self-center px-8 py-2.5 rounded-lg"
              />
            </div>
          </form>
        </div>
        </div>
      </div>
    </div>
  );
};
